import { toFullName } from '@repo/toolkit/to-full-name'

import { AffectEnrollmentInput } from '../enrollment/affect-enrollment'
import { CenterEnrollmentAgeCompliance } from './center-age-compliance'

type IncompatibleAgeRow = { enrollmentId: number; name: string; age: number | null }

export class CenterAgeComplianceProperties {
  /** Enrollment ids currently assigned to each center, ids sorted so query keys stay stable. */
  static groupEnrollmentIdsByCenter(input: AffectEnrollmentInput[]) {
    const assignments = new Map<number, number[]>()
    input.forEach((i) => {
      if (!i.center) return
      assignments.set(i.center.id, [...(assignments.get(i.center.id) ?? []), i.enrollmentId])
    })
    return [...assignments.entries()].map(([centerId, ids]) => ({
      centerId,
      enrollmentIds: ids.sort((a, b) => a - b)
    }))
  }

  static mapByEnrollmentId(compliances: (CenterEnrollmentAgeCompliance[] | undefined)[]) {
    const map = new Map<number, CenterEnrollmentAgeCompliance>()
    compliances.forEach((list) =>
      list?.forEach((compliance) => map.set(compliance.enrollmentId, compliance))
    )
    return map
  }

  static isIncompatible(compliance?: CenterEnrollmentAgeCompliance): boolean {
    return compliance?.isAgeCompliant === false
  }

  static isMinAgeViolated(compliance?: CenterEnrollmentAgeCompliance): boolean {
    if (!compliance || compliance.minAge === null || compliance.ageAtDeparture === null)
      return false
    return compliance.ageAtDeparture < compliance.minAge
  }

  static isMaxAgeViolated(compliance?: CenterEnrollmentAgeCompliance): boolean {
    if (!compliance || compliance.maxAge === null || compliance.ageAtReturn === null) return false
    return compliance.ageAtReturn > compliance.maxAge
  }

  static getViolations(compliance?: CenterEnrollmentAgeCompliance) {
    return {
      isAgeIncompatible: this.isIncompatible(compliance),
      isMinAgeViolated: this.isMinAgeViolated(compliance),
      isMaxAgeViolated: this.isMaxAgeViolated(compliance)
    }
  }

  /** Rows to list in the "age incompatible" confirmation, in the order of the affectation input. */
  static getIncompatibleRows(
    input: AffectEnrollmentInput[],
    enrollments: { id: number; childFirstName?: string; childLastName?: string }[],
    complianceByEnrollmentId: Map<number, CenterEnrollmentAgeCompliance>
  ): IncompatibleAgeRow[] {
    return input
      .filter(({ enrollmentId }) =>
        this.isIncompatible(complianceByEnrollmentId.get(enrollmentId))
      )
      .map(({ enrollmentId }) => {
        const enrollment = enrollments.find((e) => e.id === enrollmentId)
        return {
          enrollmentId,
          name: toFullName(enrollment?.childFirstName, enrollment?.childLastName, {
            options: { mode: 'lastname-firstname' }
          }),
          age: complianceByEnrollmentId.get(enrollmentId)?.ageAtDeparture ?? null
        }
      })
  }
}
